import React from 'react';

// Recovers from a failed dynamic import (a React.lazy code-split chunk). This happens when the
// bundle a running tab references no longer exists on the server:
//   • dev — a recompile changed chunk hashes and a direct URL / hard reload requests a now-404 chunk;
//   • prod — an open tab requests a pre-deploy chunk after a new deploy.
// Either way the import rejects with a ChunkLoadError and the surrounding <Suspense> would otherwise
// sit on its "Loading…" fallback forever ("stale mode"). We reload once to fetch the current bundle
// (rate-limited so a genuinely broken chunk can't loop); a persistent failure shows a manual retry.
// NON-chunk errors are re-thrown untouched so existing behaviour / any parent boundary is preserved.
const CHUNK_RE = /ChunkLoadError|Loading chunk [\w-]+ failed|Loading CSS chunk|dynamically imported module/i;
const isChunkError = (e) => !!e && CHUNK_RE.test(`${e.name || ''} ${e.message || ''}`);
const RELOAD_KEY = 'chunk-reload-ts';
const RELOAD_MIN_GAP_MS = 10000;

export default class ChunkErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }

  static getDerivedStateFromError(error) { return { error }; }

  componentDidCatch(error) {
    if (!isChunkError(error)) return;
    try {
      const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
      const now = Date.now();
      if (now - last > RELOAD_MIN_GAP_MS) {
        sessionStorage.setItem(RELOAD_KEY, String(now));
        window.location.reload();
      }
    } catch {
      window.location.reload();
    }
  }

  render() {
    const { error } = this.state;
    if (error) {
      if (!isChunkError(error)) throw error;   // pass non-chunk errors through to normal handling
      // Chunk error: a reload was likely triggered; this shows only if it was rate-limited (persistent).
      return (
        <section className="scaffold" style={{ textAlign: 'center', padding: 48 }}>
          <strong>Updating…</strong>
          <p style={{ color: 'var(--text-2, #6a786e)', marginTop: 8 }}>
            The app was updated. If it doesn’t refresh on its own, reload the page.
          </p>
          <button className="btn success sm"
            type="button"
            onClick={() => { try { sessionStorage.removeItem(RELOAD_KEY); } catch { /* quota */ } window.location.reload(); }}
            style={{ marginTop: 14 }}>
            Reload
          </button>
        </section>
      );
    }
    return this.props.children;
  }
}
